/**
 * legacyCopy entries for missing-data issues and the shared UNKNOWN
 * fallback. Ported verbatim from `SuggestedActionForMissingData`,
 * `getMissingDataErrorDetailsText` in `../../ParticipantDialog.tsx`.
 * (The `ASSIGNEE_BOUNCE` entry, also ported from that file, now lives in
 * `./sentItem.tsx` next to `SENT_ITEM__POSTMARK_BOUNCED` — see the comment
 * there.)
 */
import { Link } from '@material-ui/core';
import React, { ReactNode } from 'react';
import { LightMdToJsx } from '../../../../../../services/formatter';
import { CopyPack, Issue, IssueKind } from '../types';

const ctxOf = (group: Issue[]) => group[0].ctx;
const md = (text: string | null): ReactNode => (text ? <LightMdToJsx>{text}</LightMdToJsx> : null);

export const miscLegacyCopy: Partial<CopyPack> = {
  [IssueKind.MISSING_ACTOR]: {
    category: 'Missing data',
    summary: (g) => {
      const d = (g[0].raw as { family: 'MISSING_DATA'; data: any }).data;
      return ctxOf(g).t(`Missing supporting actor - **{{ actor }}**`, { actor: d.actor.name });
    },
    whatCausedIt: (g) => miscLegacyCopy[IssueKind.MISSING_ACTOR]!.summary(g),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const d = (g[0].raw as { family: 'MISSING_DATA'; data: any }).data;
      if (!c.onOpenParticipant) return null;
      return (
        <div>
          <LightMdToJsx>{c.t('Add this supporting actor to the ')}</LightMdToJsx>
          <Link onClick={() => c.onOpenParticipant?.(d.instance)}>
            {c.t(`Participant's experience`)}
          </Link>
        </div>
      );
    }
  },
  [IssueKind.MISSING_VAR]: {
    category: 'Missing data',
    summary: (g) => {
      const c = ctxOf(g);
      const d = (g[0].raw as { family: 'MISSING_DATA'; data: any }).data;
      return d.missingVar.length === 1
        ? c.t(`Variable was missing - **{{ missingVar }}**`, { missingVar: d.missingVar[0] })
        : c.t(`Variable was missing - **{{ missingVar }}** and **{{ more }} more**`, {
            missingVar: d.missingVar[0],
            more: d.missingVar.length - 1
          });
    },
    whatCausedIt: (g) => miscLegacyCopy[IssueKind.MISSING_VAR]!.summary(g),
    howToFixIt: (g) => md(ctxOf(g).t('Add the missing data to the participant and retry'))
  },
  [IssueKind.MISSING_DATA__UNKNOWN]: {
    category: 'Missing data',
    summary: (g) => ctxOf(g).t('Unknown error'),
    whatCausedIt: (g) => ctxOf(g).t('Unknown error'),
    howToFixIt: (g) => md(ctxOf(g).t('Please try again or contact Support for help'))
  },

  [IssueKind.UNKNOWN]: {
    category: 'Other',
    summary: () => '',
    whatCausedIt: () => null,
    howToFixIt: () => null
  }
};
